import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';
import newslitLogo from '../../../assets/images/newslit-logo.svg';
import rocketIllustration from '../../../assets/images/rocket-illustration.png';
import ErrorReporter from '../../../components/error-reporter/ErrorReporter';
import TwitterLoginBtn from '../../../components/twitter-login-btn/TwitterLoginBtn';
import Input from '../../../components/ui/Input';
import { useAuthFlow, useSession } from '../../../state/app-state';
import DefaultPageLayout from '../../../layouts/defaultPageLayout';
import { finalizeSignInWithTwitter, requestSignIn } from '../../../hooks/useAuth';
import { isLocalStorageAvailable } from '../../../services/authentication';

const SignInPage: React.FC = () => {
  const history = useHistory();
  const session = useSession();
  const authFlow = useAuthFlow();
  const { search: searchLocation } = useLocation();

  // TODO: Control via SiteFlag
  const showTwitterLogin = true;
  const isTwitterIntegrationActive = true;

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: yup.object().shape({
      email: yup.string().email().required('Please type a valid email'),
      password: yup.string().required('Please type your password'),
    }),
    onSubmit: values => {
      requestSignIn(values.email, values.password);
    },
  });

  useEffect(() => {
    // if account is already logged in, redirect them to the dashboard page
    if (session?.account && session?.token) {
      const searchParams = new URLSearchParams(searchLocation);
      const redirectAfterSignInPath = searchParams.get('next');
      const redirectPath = !!redirectAfterSignInPath ? redirectAfterSignInPath : '/dashboard';
      history.push(redirectPath);
    }
  }, [session, history, searchLocation]);

  return (
    <DefaultPageLayout title="Sign in to Newslit">
      <div className="min-h-screen bg-white flex">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              <img className="h-12 w-auto" src={newslitLogo} alt="Newslit" />
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Sign in to your account
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Or{' '}
                <a
                  href={`/users/signup`}
                  className="font-medium text-secondary-dark hover:text-secondary"
                >
                  start your 14-day free trial
                </a>
              </p>
            </div>

            {authFlow.isAccountDeactivated && (
              <div className="mt-6 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800 m-0">
                      Your account has been deactivated
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              {showTwitterLogin && (
                <>
                  <div className="mt-1">
                    <TwitterLoginBtn
                      disabled={!isTwitterIntegrationActive}
                      checkIfEnable={() => isLocalStorageAvailable()}
                      onSuccess={() => finalizeSignInWithTwitter()}
                    />
                  </div>

                  <div className="mt-6 relative">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or continue with</span>
                    </div>
                  </div>
                </>
              )}

              <div className="mt-6">
                <form onSubmit={formik.handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <div className="mt-1">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        error={
                          formik.touched.email && formik.errors.email
                            ? formik.errors.email
                            : undefined
                        }
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="mt-1 relative rounded-md shadow-xs">
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        error={
                          formik.touched.password && formik.errors.password
                            ? formik.errors.password
                            : undefined
                        }
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm ml-auto">
                      <a
                        href="/users/password_reset"
                        className="font-medium text-secondary-dark hover:text-secondary"
                      >
                        Forgot your password?
                      </a>
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={authFlow.isFetching || !formik.isValid}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:opacity-50"
                    >
                      {authFlow.isFetching && (
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      )}
                      Sign in
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="hidden lg:block relative w-0 flex-1">
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src={rocketIllustration}
            alt="Newslit Illustration"
          />
        </div>
      </div>

      <ErrorReporter />
    </DefaultPageLayout>
  );
};

export default SignInPage;
